import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

const STORAGE_KEY = 'viewMode-preference';
const MOBILE_QUERY = '(max-width: 767px)';

interface ViewModeContextValue {
  /** Resolved value: respects manual override, falls back to auto-detection */
  isMobile: boolean;
  /** Raw matchMedia result (ignores manual override) */
  isNativelyMobile: boolean;
  /** Whether currently using auto-detection or manual override */
  viewModeSource: 'auto' | 'manual';
  /** Toggle between mobile/desktop (sets manual override) */
  toggleViewMode: () => void;
  /** Clear manual override, revert to auto-detection */
  resetToAuto: () => void;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [isNativelyMobile, setIsNativelyMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  const [manualOverride, setManualOverride] = useState<'mobile' | 'desktop' | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'mobile' || stored === 'desktop') return stored;
    return null;
  });

  // Listen for viewport changes
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsNativelyMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const isMobile = manualOverride ? manualOverride === 'mobile' : isNativelyMobile;
  const viewModeSource: 'auto' | 'manual' = manualOverride ? 'manual' : 'auto';

  // Sync body class for CSS-level targeting
  useEffect(() => {
    document.documentElement.classList.toggle('mobile-view', isMobile);
    document.documentElement.classList.toggle('desktop-view', !isMobile);
  }, [isMobile]);

  const toggleViewMode = useCallback(() => {
    const newValue = isMobile ? 'desktop' : 'mobile';
    setManualOverride(newValue);
    localStorage.setItem(STORAGE_KEY, newValue);
  }, [isMobile]);

  const resetToAuto = useCallback(() => {
    setManualOverride(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <ViewModeContext.Provider
      value={{ isMobile, isNativelyMobile, viewModeSource, toggleViewMode, resetToAuto }}
    >
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): ViewModeContextValue {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
}
